/**
 * GameCountdown + NearEventStartPulse — pre-game activity indicators
 * shared by the moneyline GameTable and the SpreadsTotals MatchupRow so
 * both views read identically.
 *
 *   <GameCountdown>           → muted "(2h 15m)" / "(45:23)" / "(2d)"
 *                               next to the local event start time.
 *                               Disappears once the game starts (LIVE
 *                               pulse takes over).
 *   <NearEventStartPulse>     → small animate-pulse dot beside the
 *                               date, hue-ramped from yellow (3h out)
 *                               → red (0h). Mirrors the live-game
 *                               green pulse exactly so the visual
 *                               language stays consistent.
 *
 * Both components share the module-level `useNow()` ticker so all
 * countdowns on a page run off one setInterval.
 */

import { useNow } from '@/lib/infra/useNow';

// Near-start proximity window + hue ramp: within 3h of the event, ramps from
// yellow (3h out) → red (0h). Shared by the date-column pulse dot and the
// Score-column countdown so the two read as the same cue. Returns null when
// the event is >3h out or already started (caller renders the muted / no-dot
// state). 60 = yellow, 0 = red in HSL hue.
const NEAR_START_WINDOW_MS = 3 * 3_600_000;
export function nearStartColor(diffMs: number): string | null {
  if (diffMs <= 0 || diffMs > NEAR_START_WINDOW_MS) return null;
  const hoursUntil = diffMs / 3_600_000;
  return `hsl(${(Math.max(0, hoursUntil) / 3) * 60}, 90%, 55%)`;
}

export function GameCountdown({
  startTimeMs,
  className,
}: {
  startTimeMs: number;
  /** Size/tone override. Defaults to the muted 10px inline chip; the
   *  moneyline Score column passes a larger size for the stacked layout. */
  className?: string;
}) {
  const now = useNow();
  const diff = startTimeMs - now;
  if (diff <= 0) return null;
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  let text: string;
  if (days >= 1) {
    text = `${days}d`;
  } else {
    const hours = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    if (hours >= 1) {
      text = `${hours}h ${mins}m`;
    } else {
      const secs = totalSec % 60;
      text = `${mins}:${secs.toString().padStart(2, '0')}`;
    }
  }
  // Match the date-column pulse dot's color within the 3h window so the
  // countdown and the dot read as one cue; muted gray when further out.
  const color = nearStartColor(diff);
  return (
    <span
      className={`font-mono ${className ?? 'text-[10px]'} ${color ? '' : 'text-muted-foreground'}`}
      style={color ? { color } : undefined}
      title={`Event start in ${text}`}
    >
      ({text})
    </span>
  );
}

/**
 * LiveBadge — a clear, unmissable "LIVE" chip for a game that has started,
 * styled to mirror the FINAL chip (same slot, same shape) so the trader can't
 * mistake a live market for a pregame one. Carries its own pulse dot.
 *
 * Trading-safety ask (StreamRift #1): a trader opened pregame-style orders on
 * an already-live game because the only cue was a 6px dot. This makes "this
 * game is live" impossible to miss.
 */
export function LiveBadge({ className }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border border-green-500/60 bg-green-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-400 ${className ?? ''}`}
      title="Live — game has started; the book is actively trading"
    >
      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
      Live
    </span>
  );
}

export function NearEventStartPulse({ startTimeMs }: { startTimeMs: number }) {
  const now = useNow();
  const diff = startTimeMs - now;
  const color = nearStartColor(diff);
  if (color == null) return null;
  const totalSec = Math.floor(diff / 1000);
  const mins = Math.floor(totalSec / 60);
  return (
    <span
      className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full"
      style={{ backgroundColor: color }}
      title={`Event start in ${mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`}`}
    />
  );
}
